<template>
    <entity-editor
        entity="encounter"
        :id="id"
        :new-entity="newEncounter"
        :copy="copy"
        @isDirtyChange="handleIsDirtyChange"
        @entityLoaded="encounterLoaded"
    >
        <template v-slot:[`editor`]="{ doc }">
            <v-row>
                <v-col cols="12" sm="4">
                    <v-select
                        v-model="doc.eventId"
                        :rules="[requiredRule]"
                        :items="events"
                        item-text="name"
                        item-value="id"
                        :disabled="isLoading"
                        label="Event"
                        outlined
                        dense
                        :readonly="isEncounterTimeLocked"
                        @change="eventIdChanged"
                    >
                        <template v-slot:[`label`]>
                            <label-required name="Event" />
                        </template>
                    </v-select>
                </v-col>
                <v-col cols="12" sm="4">
                    <v-text-field
                        v-model="dayAndTime"
                        outlined
                        readonly
                        dense
                        label="Encounter Day and Time"
                        @click="showSlotPicker = !isEncounterTimeLocked"
                    >
                        <template v-slot:[`label`]>
                            <label-required name="Encounter Day and Time" />
                        </template>
                    </v-text-field>
                </v-col>
                <v-col cols="12" sm="4">
                    <template>
                        <v-combobox
                            v-model="doc.tags"
                            :items="encounterTags"
                            chips
                            clearable
                            label="Encounter Tags"
                            multiple
                            outlined
                        >
                            <template v-slot:selection="{ attrs, item, select, selected }">
                                <v-chip
                                    v-bind="attrs"
                                    :input-value="selected"
                                    close
                                    @click="select"
                                    @click:close="doc.tags.splice(doc.tags.indexOf(item), 1)"
                                >
                                    <strong>{{ item }}</strong>
                                </v-chip>
                            </template>
                        </v-combobox>
                    </template>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" sm="4">
                    <v-select
                        v-model="doc.encounterType"
                        :rules="[requiredRule]"
                        :items="encounterTypes"
                        :disabled="isLoading"
                        label="Encounter Type"
                        outlined
                        dense
                    >
                        <template v-slot:[`label`]>
                            <label-required name="Encounter Type" />
                        </template>
                    </v-select>
                </v-col>
                <v-col cols="12" sm="4">
                    <v-select
                        v-model="doc.priority"
                        :rules="[requiredRule]"
                        :items="priorities"
                        :disabled="isLoading"
                        label="Priority"
                        outlined
                        dense
                    >
                        <template v-slot:[`label`]>
                            <label-required name="Priority" />
                        </template>
                    </v-select>
                </v-col>
                <v-col cols="12" sm="4">
                    <v-select
                        v-model="doc.refCoverNeeded"
                        :items="refCover"
                        :disabled="isLoading"
                        label="Ref Cover Required"
                        outlined
                        dense
                    >
                    </v-select>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12">
                    <v-autocomplete
                        v-model="doc.plotKeyId"
                        :rules="[requiredRule]"
                        :items="sortedPlots"
                        :disabled="isLoading"
                        label="Plot"
                        outlined
                        dense
                        clearable
                    >
                        <template v-slot:[`label`]>
                            <label-required name="Plot" />
                        </template>
                        <template v-slot:selection="data">
                            {{ data.item.text }}
                            <approval-status :status="data.item.statusId"></approval-status>
                        </template>
                        <template v-slot:item="data">
                            {{ data.item.text }}
                            <approval-status :status="data.item.statusId"></approval-status>
                        </template>
                    </v-autocomplete>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12">
                    <v-text-field v-model="doc.name" :rules="[requiredRule]" outlined dense>
                        <template v-slot:[`label`]>
                            <label-required name="Name" />
                        </template>
                    </v-text-field>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" sm="3">
                    <v-select
                        v-model="doc.inbound"
                        :items="inOutOptions"
                        label="Inbound"
                        rows="2"
                        outlined
                        dense
                        :rules="[requiredRuleValidZero]"
                    ></v-select>
                </v-col>
                <v-col cols="12" sm="9">
                    <v-autocomplete
                        v-if="doc.inbound === 1"
                        v-model="doc.inboundCircleKeyId"
                        :items="transportCircles"
                        outlined
                        dense
                        label="Transport in from..."
                        clearable
                        open-on-clear
                        :rules="[requiredRule]"
                    >
                    </v-autocomplete>
                    <v-textarea
                        v-if="doc.inbound > 1"
                        v-model="doc.inboundDetails"
                        label="Inbound Details"
                        rows="2"
                        outlined
                        auto-grow
                        :rules="[requiredRule]"
                    ></v-textarea>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" sm="3">
                    <v-select
                        v-model="doc.outbound"
                        :items="inOutOptions"
                        label="Outbound"
                        rows="2"
                        outlined
                        dense
                        :rules="[requiredRuleValidZero]"
                    ></v-select>
                </v-col>
                <v-col cols="12" sm="9">
                    <v-autocomplete
                        v-if="doc.outbound === 1"
                        v-model="doc.outboundCircleKeyId"
                        :items="transportCircles"
                        outlined
                        dense
                        label="Transport out to..."
                        clearable
                        open-on-clear
                        :rules="[requiredRule]"
                    >
                    </v-autocomplete>
                    <v-textarea
                        v-if="doc.outbound > 1"
                        v-model="doc.outboundDetails"
                        label="Outbound Details"
                        rows="2"
                        outlined
                        auto-grow
                        :rules="[requiredRule]"
                    ></v-textarea>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12">
                    <v-textarea v-model="doc.description" label="Description" rows="5" outlined auto-grow></v-textarea>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" sm="3">
                    <v-select
                        v-model="doc.costumeLevel"
                        :items="costumeLevels"
                        label="Costume Level"
                        rows="2"
                        outlined
                        dense
                    ></v-select>
                </v-col>
                <v-col cols="12" sm="9">
                    <v-textarea
                        v-model="doc.costumeNotes"
                        label="Costume"
                        rows="2"
                        outlined
                        auto-grow
                        :disabled="doc.costumeLevel === 0"
                        :background-color="
                            doc.costumeLevel === 0 ? ($vuetify.theme.dark ? `grey darken-4` : `grey lighten-2`) : ''
                        "
                    ></v-textarea>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" sm="3">
                    <v-select
                        v-model="doc.makeupLevel"
                        :items="makeupLevels"
                        label="Makeup Notes"
                        rows="2"
                        outlined
                        dense
                    ></v-select>
                </v-col>
                <v-col cols="12" sm="9">
                    <v-textarea
                        v-model="doc.makeupNotes"
                        label="Makeup Notes"
                        rows="2"
                        outlined
                        auto-grow
                        :disabled="doc.makeupLevel === 0"
                        :background-color="
                            doc.makeupLevel === 0 ? ($vuetify.theme.dark ? `grey darken-4` : `grey lighten-2`) : ''
                        "
                    ></v-textarea>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" sm="2"><span class="font-weight-medium">Monsters</span></v-col>
            </v-row>
            <v-divider class="my-3" light></v-divider>
            <v-row>
                <v-col cols="12" sm="1">
                    <v-text-field v-model="newMonster.minQuantity" outlined type="number" dense min="0" />
                </v-col>
                <v-col cols="12" sm="1">
                    <v-text-field v-model="newMonster.maxQuantity" outlined type="number" dense min="0" />
                </v-col>
                <v-col cols="12" sm="9">
                    <monster-picker :plot-id="doc.plotKeyId" @selected="getMonster" />
                </v-col>
                <v-col cols="12" sm="1">
                    <v-btn title="Add Monster" color="success" @click="addMonster(doc)" :disabled="!canAddMonster">
                        <v-icon>mdi-plus</v-icon>
                    </v-btn>
                </v-col>
            </v-row>
            <v-row v-if="newMonster.stats">
                <v-col cols="12" sm="10">
                    <monster-summary :monster="newMonster.stats"></monster-summary>
                </v-col>
            </v-row>
            <v-divider class="my-3" light></v-divider>
            <v-row v-for="(monster, i) in doc.monsters" :key="i">
                <v-col cols="12" sm="1">
                    <v-text-field v-model="monster.minQuantity" outlined type="number" dense min="0" />
                </v-col>
                <v-col cols="12" sm="1">
                    <v-text-field v-model="monster.maxQuantity" outlined type="number" dense min="0" />
                </v-col>
                <v-col cols="12" sm="9">
                    <v-expansion-panels accordion tile hover>
                        <v-expansion-panel>
                            <v-expansion-panel-header class="py-1 pl-3">
                                <span>
                                    <span class="mr-2">{{ monster.stats.name }}</span>
                                    <approval-status :status="monster.stats.statusId"></approval-status>
                                </span>
                            </v-expansion-panel-header>
                            <v-expansion-panel-content>
                                <monster-summary :monster="monster.stats"></monster-summary>
                            </v-expansion-panel-content>
                        </v-expansion-panel>
                    </v-expansion-panels>
                    <v-textarea
                        v-model="monster.notes"
                        label="Notes"
                        outlined
                        rows="1"
                        auto-grow
                        class="mt-3"
                    ></v-textarea>
                </v-col>
                <v-col cols="12" sm="1">
                    <v-btn title="Remove Monster" color="error" @click="removeMonster(doc, monster.stats.keyId)">
                        <v-icon>mdi-minus</v-icon>
                    </v-btn>
                </v-col>
            </v-row>
            <v-dialog v-model="showSlotPicker" max-width="900">
                <v-card tile data-card>
                    <v-toolbar dark color="secondary">
                        <v-btn icon dark data-close @click="showSlotPicker = false">
                            <v-icon>mdi-close</v-icon>
                        </v-btn>
                        <v-toolbar-title>{{ event ? event.name : '' }} - Select Encounter Time</v-toolbar-title>
                    </v-toolbar>
                    <v-card-text>
                        <event-time-table
                            :event="event"
                            @slotSelected="(eventDayId, time) => handleSlotSelected(doc, eventDayId, time)"
                            :slot-data="slotData"
                            is-select-mode
                        ></event-time-table>
                    </v-card-text>
                </v-card>
            </v-dialog>
        </template>
    </entity-editor>
</template>
<script>
import api from '@/api'
import sortFilters from '@/components/sortFilters'
import { DateTime } from 'luxon'
import { mapState } from 'vuex'
import navGuardConfirmMixin from '../../mixins/navGuardConfirmMixin'
import editorMixin from '../../mixins/editorMixin'
import RequiresLabel from '../../components/RequiredLabel'
import MonsterSummary from '../../components/MonsterSummary'
import EventTimeTable from '../../components/EventTimeTable'
import ApprovalStatusIcon from '../../components/ApprovalStatusIcon'
import MonsterPicker from '../../components/MonsterSelector.vue'
export default {
    name: 'EncounterEditor',
    mixins: [navGuardConfirmMixin, editorMixin],
    components: {
        'label-required': RequiresLabel,
        'monster-summary': MonsterSummary,
        'event-time-table': EventTimeTable,
        'approval-status': ApprovalStatusIcon,
        'monster-picker': MonsterPicker,
    },
    props: {
        id: {
            type: [Number, String],
            required: false,
            validator: value => {
                return !!value
            },
        },
        copy: {
            type: Boolean,
            required: false,
            default: false,
        },
        startingEvent: {
            type: Number,
            required: false,
            default: null,
        },
        startingDay: {
            type: Number,
            required: false,
            default: null,
        },
        startingTime: {
            type: Number,
            required: false,
            default: null,
        },
        encounterType: {
            type: Number,
            required: false,
            default: null,
        },
    },
    data() {
        return {
            event: null,
            dayAndTime: null,
            showSlotPicker: false,
            requiredRuleValidZero: v => v === 0 || !!v || 'Required',
            slotData: [],
            plots: [],
            transportCircles: [],
            encounterTags: [],
            priorities: [
                { text: 'Unkonwn', value: 0 },
                { text: 'Pri 1 - Vital to Event Plot; Must run on time', value: 1 },
                { text: 'Pri 2 - Vital to Event Plot; Not time critical', value: 2 },
                { text: 'Pri 3 - Essential Plot; Must run on time', value: 3 },
                { text: 'Pri 4 - Essential Plot; Not time critical', value: 4 },
                { text: 'Pri 5 - Useful Plot; Not Essential', value: 5 },
                { text: 'Pri 6 - Flavour Plot; Lowest Priority', value: 6 },
            ],
            refCover: [
                { value: 0, text: 'None' },
                { value: 1, text: 'Staff Marshal Required' },
                { value: 2, text: 'Staff Ref Required' },
                { value: 3, text: 'Stealth Ref Required' },
                { value: 4, text: 'Ref Team Required' },
            ],
            costumeLevels: [
                { value: 0, text: 'None/Costume Supplied' },
                { value: 1, text: 'Basic' },
                { value: 2, text: 'Character' },
            ],
            makeupLevels: [
                { value: 0, text: 'None' },
                { value: 1, text: 'Simple (one colour)' },
                { value: 2, text: 'Moderate (simple design more than one colour)' },
                { value: 3, text: 'Complex (complex design, prosthetics or unique character)' },
            ],
            inOutOptions: [
                { value: 0, text: 'None' },
                { value: 1, text: 'Transport' },
                { value: 2, text: 'Walk' },
                { value: 3, text: 'Appear/Disappear' },
                { value: 4, text: 'Transport (Off Plane)' },
                { value: 5, text: 'Transport (Off Network)' },
            ],
            monsters: [],
            newMonster: {
                id: null,
                minQuantity: 0,
                maxQuantity: 0,
                stats: 0,
                notes: '',
            },
        }
    },
    computed: {
        ...mapState(['user']),
        userDefaultTeamId() {
            if (this.user && this.user.defaultTeam) {
                let teamId = parseInt(this.user.defaultTeam)
                if (!isNaN(teamId)) return teamId
            }
            return -1
        },
        canAddMonster() {
            return !!this.newMonster.stats
        },
        isEncounterTimeLocked() {
            return this.encounterType && this.encounterType > 2
        },
        encounterTypes() {
            const defaultTypes = [
                { text: 'Unknown', value: 0 },
                { text: 'Non Combat', value: 1 },
                { text: 'Combat', value: 2 },
                { text: 'Non Combat To Combat', value: 6 },
                { text: 'Ritual', value: 5 },
            ]

            switch (this.encounterType) {
                case 3:
                    return [{ text: 'Mass Combat', value: 3 }]
                case 4:
                    return [{ text: 'Linear', value: 4 }]
                default:
                    return defaultTypes
            }
        },
        sortedPlots() {
            return sortFilters.sortByDefaultTeam(this.plots, this.userDefaultTeamId)
        },
    },
    mounted() {
        this.getPlots()
        this.getRitualCircles()
        this.getTags()
        if (!this.monsters || this.monsters.length === 0) {
            this.getMonsters()
        }
    },
    methods: {
        newEncounter() {
            let encounter = {
                teamId: null,
                plotKeyId: null,
                eventId: this.startingEvent ?? this.nextEvent,
                name: '',
                encounterType: this.encounterType ?? null,
                priority: null,
                areNPCsSupplied: false,
                isMonsterRoomToRun: false,
                description: null,
                costumeLevel: 0,
                costumeNote: null,
                makeupLevel: 0,
                makeupNotes: null,
                isStealth: false,
                isCarded: false,
                inbound: null,
                outbound: null,
                inboundCircleKeyId: null,
                outboundCircleKeyId: null,
                inboundDetails: null,
                outboundDetails: null,
                eventDayId: this.startingDay ?? 0,
                timeAsMinutes: this.startingTime ?? null,
                monsters: [],
            }
            this.encounterLoaded(encounter)
            return encounter
        },
        encounterLoaded(encounter) {
            if (encounter && encounter.eventId) {
                let selectedEvent = this.events.find(x => x.id === encounter.eventId)
                if (!selectedEvent) {
                    this.event = null
                    this.dayAndTime = null
                    return
                }
                this.event = selectedEvent
                let day = selectedEvent.days.find(x => x.id === encounter.eventDayId)

                if (!day || !this.isValidDate(day.date)) {
                    this.dayAndTime = null
                } else {
                    let dateOnly = DateTime.fromISO(day.date, { zone: 'utc' })
                    if (encounter.timeAsMinutes) {
                        let dateTime = dateOnly.plus({ minutes: encounter.timeAsMinutes })
                        this.dayAndTime = `${dateOnly.toFormat('cccc')} ${dateTime.toLocaleString(
                            DateTime.TIME_24_SIMPLE,
                        )}`
                    } else {
                        this.dayAndTime = `${dateOnly.toFormat('cccc')} - Floating`
                    }
                }
                this.slotData = [
                    {
                        dayId: encounter.eventDayId,
                        time: encounter.timeAsMinutes,
                        text: 'Current',
                        id: encounter.keyId,
                    },
                ]
            }
        },
        eventIdChanged(val) {
            let selectedEvent = this.events.find(x => x.id === val)
            if (!selectedEvent) {
                this.event = null
                this.dayAndTime = null
                return
            }
            this.event = selectedEvent
            this.dayAndTime = null
        },
        getPlots() {
            this.isLoading = true
            let apiUrl = `/plots`
            api.get(apiUrl)
                .then(res => {
                    this.plots = res.map(x => {
                        let team = this.teamCount > 1 || this.user.lammie.all ? `(${x.teamName}) ` : ''
                        x.text = `${team}${x.name}`
                        x.value = x.keyId
                        return x
                    })
                })
                .finally(() => (this.isLoading = false))
        },
        getRitualCircles() {
            this.isLoading = true
            let apiUrl = `/ritualCircles/active`
            api.get(apiUrl)
                .then(res => {
                    let circles = res.map(x => {
                        x.text = x.name
                        x.value = x.keyId
                        return x
                    })
                    this.transportCircles = circles
                })
                .finally(() => (this.isLoading = false))
        },
        getMonsters() {
            this.isLoading = true
            let apiUrl = `/monsters`
            api.get(apiUrl)
                .then(res => {
                    this.monsters = res
                })
                .finally(() => (this.isLoading = false))
        },
        getMonster(val) {
            if (!val || !val.keyId) return
            this.isLoadingMonster = true
            let apiUrl = `/monsters/${val.keyId}`
            api.get(apiUrl)
                .then(res => {
                    this.newMonster.stats = res
                })
                .finally(() => (this.isLoadingMonster = false))
        },
        getDay(date) {
            if (!this.isValidDate(date)) return
            let dateOnly = DateTime.fromISO(date, { zone: 'utc' })
            return dateOnly.toFormat('cccc')
        },
        isValidDate(value) {
            if (!value || value === 'Invalid DateTime' || (DateTime.isDateTime(value) && !value.isValid)) {
                return false
            }
            return true
        },
        addMonster(doc) {
            doc.monsters.push(this.newMonster)
            this.clearNewMonster()
        },
        clearNewMonster() {
            this.newMonster = {
                minQuantity: 0,
                maxQuantity: 0,
                stats: 0,
                notes: '',
            }
        },
        removeMonster(doc, monsterKeyId) {
            let removeIndex = doc.monsters.findIndex(x => x.stats.keyId === monsterKeyId)
            if (removeIndex === -1) return
            doc.monsters.splice(removeIndex, 1)
        },
        handleSlotSelected(doc, eventDayId, time) {
            doc.eventDayId = eventDayId
            doc.timeAsMinutes = time
            this.encounterLoaded(doc)
            this.showSlotPicker = false
        },
        getTags() {
            this.isLoading = true
            let apiUrl = `/encounters/tags`
            api.get(apiUrl)
                .then(res => {
                    this.encounterTags = res
                })
                .finally(() => (this.isLoading = false))
        },
    },
}
</script>
<style scoped>
.autofill {
    background: lightblue;
}
.monsterCount {
    max-width: 40px;
}
</style>
